import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Pagination from '@mui/material/Pagination';
import { useAllVendors } from '../../resources/useAllVendors';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { usePayerData } from '../../resources/hooks/usePayerDataHook';
import { URLS } from '../../resources/constants';
import { getBranch } from '../../store/user/selectors';
import Tax1099PayerSettings from '../Tax1099PayerSettings';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

interface ReturnRow {
  id: number;
  displayName: string;
  w9Status: 'confirmed' | 'not_required' | 'needed';
  ytdGross: number;
  candidateForm: string;
  decision: 'ready' | 'below_threshold' | 'blocked';
}

export const toReturnRow = (v: any): ReturnRow => {
  const gross = Number(v.ytd_gross ?? 0);
  const formType = v.default_form_type || '1099-NEC';
  // Decision/threshold come from the server, which judges against the same
  // table form generation uses -- the UI must not restate that logic.
  const decision: 'ready' | 'below_threshold' | 'blocked' = v.year_end_decision || 'below_threshold';
  const hasW9 = Boolean(v.has_w9);

  // Below the threshold no form is due, so no W-9 is required -- that is a
  // different state from one that is required and missing, and the table used
  // to render both the same red.
  let w9Status: 'confirmed' | 'not_required' | 'needed' = 'not_required';
  if (hasW9) {
    w9Status = 'confirmed';
  } else if (decision !== 'below_threshold') {
    w9Status = 'needed';
  }

  return {
    id: v.id,
    displayName: v.display_name,
    w9Status,
    ytdGross: gross,
    candidateForm: formType,
    decision,
  };
};

const Tax1099YearEndReview: React.FC = () => {
  const { t } = useTranslation('Tax1099YearEndReview');
  const location = useLocation();
  const navigate = useNavigate();
  const { branchId, businessName, maskedEin, payerData, formattedAddress, isConfigured, hasW9, w9FileName } = usePayerData();

  const currentYear = new Date().getFullYear();
  // January reviewers are closing out last year's returns, not this year's --
  // default to the prior year until March, but let them pick either.
  const defaultYear = new Date().getMonth() < 2 ? currentYear - 1 : currentYear;
  const [taxYear, setTaxYear] = useState(defaultYear);
  const yearOptions = [currentYear, currentYear - 1];

  const [activeTab, setActiveTab] = useState<'review' | 'payer'>(
    location.pathname.includes('payer-settings') ? 'payer' : 'review'
  );

  useEffect(() => {
    if (location.pathname.includes('payer-settings')) {
      setActiveTab('payer');
    }
  }, [location.pathname]);
  const [certified, setCertified] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  // Compiled forms for this branch and year, from the API. Approving is a
  // DRAFT -> APPROVED transition over these rows, so with none the request is
  // refused server-side; the button should say so rather than fail on click.
  const [compiledFormCount, setCompiledFormCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [downloadingW9, setDownloadingW9] = useState(false);

  const {
    fetchFormList, downloadFormPdf, downloadPayerW9Pdf,
    fetchYearEndPackageStatus, approveYearEndPackage,
  } = useTax1099Api();
  const [approving, setApproving] = useState(false);

  const handleDownloadCompanyW9 = () => {
    if (!branchId) return;
    setDownloadingW9(true);
    downloadPayerW9Pdf(
      { branch_id: branchId },
      (_msg: string, blob: Blob) => {
        setDownloadingW9(false);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      },
      (err: any) => {
        setDownloadingW9(false);
        toast.error(err?.response?.data?.message || t('errors.download_w9_failed'));
      }
    );
  };


  // The Vendor Center list caps page_size at 200 server-side, so a branch
  // with more vendors needs every page fetched, not just the first.
  // Shared with the other 1099 screens. The cache key includes the tax year,
  // because ytd_gross and the threshold on each row are year-scoped -- serving
  // one year's rows to another year's review would misreport readiness.
  const { vendors, loading } = useAllVendors({ branchId, taxYear });
  const returns = useMemo(() => vendors.map(toReturnRow), [vendors]);

  // Approval is persisted server-side, keyed by branch + tax_year -- reload
  // it whenever the year changes so refreshing or switching years reflects
  // what was actually signed off, not leftover local state.
  const loadApprovalStatus = useCallback(() => {
    if (!branchId) return;
    fetchYearEndPackageStatus(
      { branch_id: branchId, tax_year: taxYear },
      (_msg: string, response: any) => {
        const data = response?.data?.data ?? {};
        setIsApproved(Boolean(data.is_approved));
        setCompiledFormCount(Number(data.total ?? 0));
      },
      () => { setIsApproved(false); setCompiledFormCount(0); }
    );
    // eslint-disable-next-line
  }, [branchId, taxYear]);

  useEffect(() => {
    setCertified(false);
    loadApprovalStatus();
  }, [loadApprovalStatus]);

  // What stops this package being signed, in the order a person would fix it.
  // Each entry is shown in the button's tooltip, because a disabled control
  // that does not say what is missing sends people hunting.
  const approvalBlockers = useMemo(() => {
    const reasons: string[] = [];
    if (!maskedEin) {
      reasons.push(t('blockers.ein_missing'));
    }
    if (compiledFormCount === 0) {
      reasons.push(t('blockers.no_compiled_returns', { taxYear }));
    }
    if (!certified) {
      reasons.push(t('blockers.certification_unchecked'));
    }
    return reasons;
  }, [maskedEin, compiledFormCount, taxYear, certified, t]);

  const clearedCount = useMemo(() => returns.filter((r) => r.decision === 'ready').length, [returns]);
  const unmappedCount = useMemo(
    () => returns.filter((r) => r.decision === 'below_threshold').length,
    [returns]
  );
  const blockedCount = useMemo(() => returns.filter((r) => r.decision === 'blocked').length, [returns]);
  const hasReturns = returns.length > 0;
  const totalCount = returns.length;

  const clearedPercent = hasReturns ? Math.round((clearedCount / totalCount) * 100) : 0;
  const unmappedPercent = hasReturns ? Math.round((unmappedCount / totalCount) * 100) : 0;
  const blockedPercent = hasReturns ? Math.max(0, 100 - clearedPercent - unmappedPercent) : 0;

  const totalPages = Math.max(1, Math.ceil(returns.length / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const paginatedReturns = useMemo(() => {
    const start = (page - 1) * pageSize;
    return returns.slice(start, start + pageSize);
  }, [returns, page, pageSize]);

  const handleApprovePackage = () => {
    if (!certified) {
      alert(t('errors.certify_before_sign_off'));
      return;
    }
    if (!branchId || approving) return;
    setApproving(true);
    approveYearEndPackage(
      { branch_id: branchId, tax_year: taxYear },
      (_msg: string, response: any) => {
        setApproving(false);
        const approved = Boolean(response?.data?.data?.is_approved);
        setIsApproved(approved);
        if (approved) {
          toast.success(`✓ ${t('toasts.package_approved')}`, { duration: 5000 });
        } else {
          toast.error(t('errors.forms_still_draft'));
        }
      },
      (err: any) => {
        setApproving(false);
        toast.error(err?.response?.data?.message || t('errors.approve_failed'));
      }
    );
  };

  const handlePreviewAllPdfs = () => {
    if (!branchId) return;
    const previewWindow = window.open('about:blank', '_blank');
    previewWindow?.document.write(t('preview.loading_window'));
    // One toast for the whole run: the result replaces the loading message
    // instead of stacking beside it (a "no forms" error used to appear next to
    // a green "Loading..." that never went away).
    const toastId = toast.loading(t('preview.loading_toast', { taxYear }));

    // "Preview all" must act on the whole set of generated forms for the
    // year, not just the first one -- a package with 40 forms is not
    // previewed by opening form #1 and calling it done.
    // ponytail: caps at 200 forms/year, same ceiling as the vendor list page;
    // paginate here too if a branch ever exceeds it.
    fetchFormList(
      { branch_id: branchId, tax_year: taxYear, page_size: 200 },
      (_msg: string, response: any) => {
        const forms = response?.data?.data?.results ?? [];
        if (forms.length === 0) {
          previewWindow?.close();
          toast.error(t('preview.no_forms', { taxYear }), { id: toastId });
          return;
        }

        const downloadOne = (form: any, openInWindow: boolean) =>
          new Promise<void>((resolve) => {
            downloadFormPdf(
              { form_id: form.id, branch_id: branchId, copy: 'B' },
              (_message: string, blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                if (openInWindow && previewWindow) {
                  previewWindow.location.href = url;
                } else {
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `${form.form_type || '1099'}_${form.recipient_legal_name || form.id}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
                window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                resolve();
              },
              () => {
                toast.error(t('preview.render_failed', { formId: form.id }));
                resolve();
              }
            );
          });

        (async () => {
          await downloadOne(forms[0], true);
          for (const form of forms.slice(1)) {
            await downloadOne(form, false);
          }
          toast.success(t('preview.rendered', { count: forms.length, taxYear }), { id: toastId });
        })();
      },
      () => {
        previewWindow?.close();
        toast.error(t('preview.load_failed'), { id: toastId });
      }
    );
  };

  const formatCurrency = (val: number) =>
    `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="py-2">
      {/* Top Header Mode Toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
        <div className="d-flex gap-2">
          <Button
            size="sm"
            style={
              activeTab === 'review'
                ? { backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff', fontWeight: 700 }
                : { backgroundColor: '#fff', borderColor: '#14509e', color: '#14509e', fontWeight: 700 }
            }
            variant={activeTab === 'review' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('review')}
          >
            📋 {t('title')}
          </Button>
          <Button
            size="sm"
            style={
              activeTab === 'payer'
                ? { backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff', fontWeight: 700 }
                : { backgroundColor: '#fff', borderColor: '#14509e', color: '#14509e', fontWeight: 700 }
            }
            variant={activeTab === 'payer' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('payer')}
          >
            ⚙️ {t('tabs.payer')}
          </Button>
        </div>
      </div>

      {activeTab === 'payer' ? (
        <Tax1099PayerSettings />
      ) : (
        <>
          {/* VIEW 4: YEAR-END REVIEW & HUMAN SIGN-OFF */}
          <div className="d-flex flex-wrap justify-content-between align-items-end mb-3 gap-3">
            <div>
              <p
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 800,
                  color: '#1687c8',
                  marginBottom: '4px',
                }}
              >
                {t('header.eyebrow', { businessName, ein: maskedEin || t('common.not_configured'), taxYear })}
              </p>
              <h1 className="h3 font-weight-bold text-dark mb-1" style={{ color: '#0d2c55', fontWeight: 900 }}>
                {t('title')}
              </h1>
              <p className="text-muted small mb-0">
                {t('header.description')}
              </p>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Form.Select
                size="sm"
                style={{ width: 110 }}
                value={taxYear}
                onChange={(event) => setTaxYear(Number(event.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{t('common.tax_year_short', { year })}</option>
                ))}
              </Form.Select>
              <Button
                variant="light"
                className="border fw-bold d-flex align-items-center gap-2"
                style={{ backgroundColor: '#edf6fc', borderColor: '#c9e7f6', color: '#14509e' }}
                onClick={handlePreviewAllPdfs}
              >
                <span>📄</span>
                <span>{t('actions.preview_all_pdfs')}</span>
              </Button>
            </div>
          </div>

          {/* PAYER IDENTITY SUMMARY CARD */}
          <div
            className="card shadow-sm border p-3 mb-4 bg-white"
            style={{ borderRadius: '16px', border: '1px solid #dfe7f0' }}
          >
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '18px' }}>🏢</span>
                <strong style={{ fontSize: '15px', color: '#0d2c55', fontWeight: 800 }}>
                  {t('payer.card_title')}
                </strong>
                {isConfigured ? (
                  <span
                    className="badge"
                    style={{ backgroundColor: '#eaf7f1', color: '#137a55', fontWeight: 700, borderRadius: '999px', fontSize: '11px', padding: '4px 8px' }}
                  >
                    ✓ {t('payer.configured')}
                  </span>
                ) : (
                  <span
                    className="badge"
                    style={{ backgroundColor: '#fffbeb', color: '#b45309', fontWeight: 700, borderRadius: '999px', fontSize: '11px', padding: '4px 8px' }}
                  >
                    ⚠️ {t('payer.needs_setup')}
                  </span>
                )}
              </div>
              <div className="d-flex gap-2">
                {hasW9 && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={downloadingW9}
                    style={{
                      borderColor: '#10b981',
                      color: '#047857',
                      backgroundColor: '#ecfdf5',
                      fontWeight: 700,
                      borderRadius: '8px',
                    }}
                    onClick={handleDownloadCompanyW9}
                    title={t('payer.w9_download_tooltip')}
                  >
                    <i className="fa fa-file-pdf-o me-1" />
                    {downloadingW9 ? t('payer.opening') : t('payer.company_w9')}
                  </Button>
                )}
                <Button
                  variant="outline-primary"
                  size="sm"
                  style={{
                    borderColor: '#14509e',
                    color: '#14509e',
                    backgroundColor: '#fff',
                    fontWeight: 700,
                    borderRadius: '8px',
                  }}
                  onClick={() => setActiveTab('payer')}
                >
                  ⚙️ {isConfigured ? t('actions.edit_payer') : t('actions.configure_payer')}
                </Button>
              </div>
            </div>
            <div className="row g-3 small">
              <div className="col-12 col-md-4">
                <span className="text-muted d-block fw-bold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('payer.legal_name')}</span>
                <span className="text-dark fw-bold" style={{ fontSize: '13px' }}>{businessName}</span>
                {payerData?.payer_dba_name && (
                  <span className="text-muted d-block small">{t('payer.dba', { name: payerData.payer_dba_name })}</span>
                )}
              </div>
              <div className="col-12 col-md-4">
                <span className="text-muted d-block fw-bold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('payer.ein_label')}</span>
                <span className="text-dark fw-bold font-monospace" style={{ fontSize: '13px' }}>{maskedEin || t('payer.no_ein')}</span>
                {hasW9 && (
                  <span className="text-success d-block small fw-semibold mt-1">
                    ✓ {t('payer.w9_on_file')}
                  </span>
                )}
              </div>
              <div className="col-12 col-md-4">
                <span className="text-muted d-block fw-bold" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('payer.address_label')}</span>
                <span className="text-dark" style={{ fontSize: '13px' }}>{formattedAddress || t('payer.no_address')}</span>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-4">
            {/* READINESS PROGRESS (Left 2/3) */}
            <div className="col-12 col-xl-8">
              <div
                className="card shadow-sm border p-4 h-100 bg-white"
                style={{ borderRadius: '16px', border: '1px solid #dfe7f0' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <strong style={{ fontSize: '15px', color: '#0d2c55', fontWeight: 700 }}>
                    {t('progress.title')}
                  </strong>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: isApproved ? '#eaf7f1' : '#eaf7f1',
                      color: '#137a55',
                      fontWeight: 700,
                      padding: '6px 12px',
                      borderRadius: '999px',
                      fontSize: '11px',
                    }}
                  >
                    {isApproved ? t('progress.package_approved') : hasReturns ? t('progress.ready_percent', { percent: clearedPercent }) : t('progress.none_compiled')}
                  </span>
                </div>

                {/* Segmented Progress Bar */}
                <div
                  className="w-100 rounded-pill overflow-hidden d-flex mb-3"
                  style={{ height: '12px', backgroundColor: '#eff2f6' }}
                >
                  <div
                    style={{ width: `${clearedPercent}%`, backgroundColor: '#16a34a' }}
                    title={`${clearedPercent}% ${t('progress.cleared')}`}
                  />
                  <div
                    style={{ width: `${unmappedPercent}%`, backgroundColor: '#f59e0b' }}
                    title={`${unmappedPercent}% ${t('progress.below_threshold')}`}
                  />
                  <div
                    style={{ width: `${blockedPercent}%`, backgroundColor: '#ef4444' }}
                    title={`${blockedPercent}% ${t('progress.w9_blocked')}`}
                  />
                </div>

                {/* Legend metrics */}
                <div className="d-flex flex-wrap small text-muted mb-4 pb-2 border-bottom" style={{ gap: '24px' }}>
                  <span className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#16a34a',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <strong className="text-dark">{clearedCount}</strong>
                    <span>{t('progress.cleared')}</span>
                  </span>
                  <span className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#f59e0b',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <strong className="text-dark">{unmappedCount}</strong>
                    <span>{t('progress.below_threshold')}</span>
                  </span>
                  <span className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#ef4444',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <strong className="text-dark">{blockedCount}</strong>
                    <span>{t('progress.w9_blocked')}</span>
                  </span>
                </div>

                <h6
                  className="fw-bold text-uppercase text-secondary mb-3"
                  style={{ fontSize: '11px', letterSpacing: '0.05em' }}
                >
                  {t('table.heading')}
                </h6>

                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr className="text-muted small">
                        <th>{t('table.contractor')}</th>
                        <th>{t('table.w9_status')}</th>
                        <th>{t('table.gross', { taxYear })}</th>
                        <th>{t('table.candidate_form')}</th>
                        <th>{t('table.decision')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-muted">
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t('table.loading')}
                          </td>
                        </tr>
                      )}
                      {!loading && returns.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center py-5 text-muted">
                            <div className="mb-2" style={{ fontSize: '28px' }}>📋</div>
                            <div className="fw-bold mb-1 text-dark">{t('empty.title')}</div>
                            <div className="small">
                              <Trans
                                t={t}
                                i18nKey="empty.description"
                                values={{ businessName, taxYear }}
                                components={{ bold: <strong /> }}
                              />
                            </div>
                            <div className="small text-muted mt-1">
                              {t('empty.hint')}
                            </div>
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        paginatedReturns.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <strong className="text-dark">{row.displayName}</strong>
                            </td>
                            <td>
                              {(() => {
                                // Three states, not two. Rendering "needed" and
                                // "not required" identically made all 547 rows
                                // red and hid the 21 that actually block filing.
                                const badge = {
                                  confirmed: { label: t('w9_status.confirmed'), bg: '#eaf7f1', fg: '#137a55' },
                                  needed: { label: t('w9_status.needed'), bg: '#fff0f1', fg: '#b13c46' },
                                  not_required: { label: t('w9_status.not_required'), bg: '#f1f5f9', fg: '#64748b' },
                                }[row.w9Status];
                                return (
                                  <span
                                    className="badge"
                                    style={{
                                      backgroundColor: badge.bg,
                                      color: badge.fg,
                                      fontWeight: 700,
                                      borderRadius: '999px',
                                      fontSize: '10px',
                                      padding: '4px 8px',
                                    }}
                                  >
                                    {badge.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <strong className="text-dark">{formatCurrency(row.ytdGross)}</strong>
                            </td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: row.candidateForm.includes('MISC') ? '#f4f0ff' : '#edf6fc',
                                  color: row.candidateForm.includes('MISC') ? '#6938ef' : '#14509e',
                                  fontWeight: 700,
                                  borderRadius: '999px',
                                  fontSize: '10px',
                                  padding: '4px 8px',
                                }}
                              >
                                {row.candidateForm}
                              </span>
                            </td>
                            <td>
                              {row.decision === 'ready' && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: '#eaf7f1',
                                    color: '#137a55',
                                    fontWeight: 700,
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    padding: '4px 8px',
                                  }}
                                >
                                  {t('decision.ready')}
                                </span>
                              )}
                              {row.decision === 'below_threshold' && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: '#eff2f6',
                                    color: '#5e6675',
                                    fontWeight: 600,
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    padding: '4px 8px',
                                  }}
                                >
                                  {t('decision.below_threshold')}
                                </span>
                              )}
                              {row.decision === 'blocked' && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: '#fff0f1',
                                    color: '#b13c46',
                                    fontWeight: 700,
                                    borderRadius: '999px',
                                    fontSize: '10px',
                                    padding: '4px 8px',
                                  }}
                                >
                                  {t('decision.blocked')}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {returns.length > 0 && (
                  <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3 border-top">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small">{t('pagination.rows_per_page')}</span>
                      <Form.Select
                        size="sm"
                        style={{ width: 85 }}
                        value={pageSize}
                        onChange={(event) => {
                          setPageSize(Number(event.target.value));
                          setPage(1);
                        }}
                      >
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="text-muted small">
                        {t('pagination.showing', {
                          from: Math.min((page - 1) * pageSize + 1, returns.length),
                          to: Math.min(page * pageSize, returns.length),
                          count: returns.length,
                        })}
                      </span>
                    </div>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_event, value) => setPage(value)}
                      size="small"
                      color="primary"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* HUMAN SIGN-OFF CARD (Right 1/3) */}
            <div className="col-12 col-xl-4">
              <div
                className="p-4 rounded-4 border d-flex flex-column justify-content-between shadow-sm h-100"
                style={{
                  border: '1px solid #bfdbfe',
                  background: 'linear-gradient(135deg, #f8fcff 0%, #edf7fc 100%)',
                  borderRadius: '16px',
                  minHeight: '380px',
                }}
              >
                <div>
                  <span
                    className="badge mb-3"
                    style={{
                      backgroundColor: '#edf6fc',
                      color: '#14509e',
                      fontWeight: 700,
                      padding: '5px 10px',
                      borderRadius: '999px',
                      fontSize: '11px',
                    }}
                  >
                    {t('sign_off.badge')}
                  </span>
                  <h2
                    className="h5 fw-bold text-dark mb-2"
                    style={{ color: '#0d2c55', fontWeight: 900 }}
                  >
                    {t('sign_off.title', { taxYear })}
                  </h2>
                  <p className="small text-muted mb-4" style={{ lineHeight: 1.5 }}>
                    <Trans
                      t={t}
                      i18nKey="sign_off.confirmation"
                      values={{ taxYear, businessName, ein: maskedEin || t('common.not_configured') }}
                      components={{ bold: <strong />, mono: <span className="font-monospace fw-bold" /> }}
                    />
                  </p>

                  <label className="d-flex align-items-start gap-2 small text-dark fw-semibold cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={certified}
                      onChange={(e) => setCertified(e.target.checked)}
                      disabled={isApproved}
                    />
                    <span>
                      {t('sign_off.certify_checkbox')}
                    </span>
                  </label>
                </div>

                <div>
                  {/* The title sits on a wrapper: a disabled button fires no
                      hover events in every browser, so a title on the button
                      itself would never be seen in the one case it matters. */}
                  <span
                    className="d-block"
                    title={
                      isApproved
                        ? t('sign_off.tooltip_approved')
                        : approvalBlockers.length
                          ? `${t('sign_off.tooltip_blocked')}\n• ${approvalBlockers.join('\n• ')}`
                          : t('sign_off.tooltip_ready')
                    }
                  >
                    <Button
                      variant="primary"
                      className="w-100 py-2 fw-bold"
                      style={{
                        backgroundColor: isApproved ? '#16a34a' : '#14509e',
                        borderColor: isApproved ? '#16a34a' : '#14509e',
                        borderRadius: '9px',
                        fontSize: '13px',
                      }}
                      onClick={handleApprovePackage}
                      disabled={isApproved || approving || approvalBlockers.length > 0}
                    >
                      {isApproved ? `✓ ${t('sign_off.approved')}` : approving ? t('sign_off.approving') : t('sign_off.approve_button')}
                    </Button>
                  </span>
                  {!isApproved && approvalBlockers.length > 0 && (
                    <ul className="text-muted mt-2 mb-0 ps-3" style={{ fontSize: '11px' }}>
                      {approvalBlockers.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  )}
                  <p className="text-muted text-center mt-2 mb-0" style={{ fontSize: '10px' }}>
                    {t('sign_off.audit_note')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Tax1099YearEndReview;
